import { useEffect, useMemo, useRef, useState } from 'react';
import { BSDashboardContext } from '../../contexts/bsDashboardContext';
import { useFetchBSWidgets } from '../../hooks/useFetchBSWidgets';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';
import { format, parse } from 'date-fns';
import { filterLocationsBySelectedDateRange, padNumberWithZero } from '../../../../helpers/utils';
import { formatBsDataForPrint } from '../../helpers/utils';
import type { WidgetsForPrintType } from '../../types/bsWidgetTypes';

const BSDashboardCore = ({ children }: { children: React.ReactNode }) => {
  const [widgetsList, setWidgetsList] = useState([]);
  const [widgetsForPrint, setWidgetsForPrint] = useState<WidgetsForPrintType | object>({});
  const userName = useSelector(
    (state: RootState) => state.company?.company_details?.user_name ?? ''
  );
  const printFormRef = useRef<HTMLFormElement>();
  const disablePrint = useMemo(() => {
    return !(widgetsList.length > 0 && Object.keys(widgetsForPrint).length === widgetsList.length);
  }, [JSON.stringify(widgetsForPrint), JSON.stringify(widgetsList)]);

  const { data, error, isLoading } = useFetchBSWidgets();

  const filters = useSelector((state: RootState) => state.filters);
  const decimalPrecision = useSelector(
    (state: RootState) => state.company?.company_details?.currency_precision ?? 0
  );
  const companyLocations = useSelector((state: RootState) => state.locations);

  const currentDateTime = new Date();
  const dateRange = filters.dates;
  const timeRange = filters.timeRange;

  const selectedDateTimeString = useMemo(() => {
    const startDate = parse(dateRange[0], 'yyyy-MM-dd', currentDateTime);
    const endDate = parse(dateRange[1], 'yyyy-MM-dd', currentDateTime);
    const startTime = timeRange[0];
    const endTime = timeRange[1];
    const startHours = padNumberWithZero(startTime[0]);
    const startMinutes = padNumberWithZero(startTime[1]);
    const endHours = padNumberWithZero(endTime[0]);
    const endMinutes = padNumberWithZero(endTime[1]);
    const startDateTimeString = `${format(
      startDate,
      'do MMMM yyyy'
    )} ${startHours}:${startMinutes}:00`;
    const endDateTimeString = `${format(endDate, 'do MMMM yyyy')} ${endHours}:${endMinutes}:00`;
    return `${startDateTimeString} - ${endDateTimeString}`;
  }, [dateRange, timeRange]);

  const selectedLocationNamesForPrint = useMemo(() => {
    const validLocations = filterLocationsBySelectedDateRange(companyLocations, dateRange);
    if (!Array.isArray(validLocations) || validLocations.length === 0) {
      return [];
    }
    const selectedValidLocations = validLocations.filter((location: any) =>
      filters.locations.includes(location.id)
    );
    if (selectedValidLocations.length === validLocations.length) {
      return [];
    }
    return selectedValidLocations.map((location: any) => location.name).filter(Boolean);
  }, [
    JSON.stringify(companyLocations),
    JSON.stringify(dateRange),
    JSON.stringify(filters.locations)
  ]);

  useEffect(() => {
    if (data) {
      setWidgetsList(data);
    }
  }, [data]);

  useEffect(() => {
    setWidgetsForPrint({});
  }, [JSON.stringify(filters)]);

  const addWidgetDataForPrint = (widgetData) => {
    setWidgetsForPrint((w) => ({ ...w, ...widgetData }));
  };

  const submitFormForPrint = () => {
    printFormRef.current?.submit();
  };

  // Format widget data for print - add commas to numbers and add decimal precision
  const formattedWidgetsForPrint = useMemo(() => {
    if (Object.keys(widgetsForPrint).length === 0) {
      return {};
    }
    // Create a new object with formatted values
    const newFormattedWidgets = Object.fromEntries(
      Object.entries(widgetsForPrint).map(([widgetName, widgetData]) => {
        if (!widgetData || typeof widgetData !== 'object') {
          return [widgetName, widgetData];
        }

        // Deep clone widgetData to prevent mutation of the original state object
        const clonedWidgetData = JSON.parse(JSON.stringify(widgetData)) as TableWidgetProperty;
        clonedWidgetData.content_data = formatBsDataForPrint(
          clonedWidgetData.content_data,
          decimalPrecision
        );
        clonedWidgetData.summary_data = formatBsDataForPrint(
          clonedWidgetData.summary_data,
          decimalPrecision
        );
        clonedWidgetData.footer_data = formatBsDataForPrint(
          clonedWidgetData.footer_data,
          decimalPrecision
        );
        return [widgetName, clonedWidgetData];
      })
    );
    return newFormattedWidgets;
  }, [widgetsForPrint, decimalPrecision]);

  return (
    <BSDashboardContext.Provider
      value={{
        widgetsList,
        widgetsForPrint,
        addWidgetDataForPrint,
        submitFormForPrint,
        handlePrint: submitFormForPrint,
        disablePrint,
        selectedDateTimeString,
        dataFetchError: error,
        isDataLoading: !data && isLoading,
        userName,
        currentDateTime
      }}
    >
      {children}
      <form
        ref={printFormRef}
        action={`/api/v1/dar/business-summary/download/html`}
        target={`_blank`}
        method="post"
        className="print"
      >
        <input
          name="business_summary_data"
          type="hidden"
          value={JSON.stringify(formattedWidgetsForPrint)}
        />
        <input
          name="location_names"
          type="hidden"
          value={
            selectedLocationNamesForPrint.length > 0
              ? JSON.stringify(selectedLocationNamesForPrint)
              : ''
          }
        />
        <input name="date_range" type="hidden" value={selectedDateTimeString} />
        <input
          name="current_date_time"
          type="hidden"
          value={format(currentDateTime, 'do MMMM yyyy - HH:mm:ss')}
        />
        <input name="jwt" type="hidden" value={localStorage.getItem('jwt') ?? ''} />
      </form>
    </BSDashboardContext.Provider>
  );
};

export default BSDashboardCore;
